import { useCallback, useContext, useEffect, useState, createContext, ReactNode, RefObject } from 'react';
import { useEditorNavigate, useEditorSearch } from 'app/router/params';

export interface EditorContextValue {
  editorContainerRef: RefObject<HTMLDivElement | null>;
  shadowHost?: (HTMLElement & { shadowRoot: ShadowRoot | null }) | null;
  documentRevisionId?: string;
}

export const EditorContext = createContext<EditorContextValue>({} as EditorContextValue);

export const useEditorContext = (): EditorContextValue => useContext(EditorContext);

export interface EditorModeContextValue {
  mode: 'tagging' | 'editing';
  setMode: (mode: 'tagging' | 'editing') => void;
  setTaggingMode: () => void;
  setEditingMode: () => void;
  isTaggingMode: boolean;
  isEditingMode: boolean;
  taggingSubMode: 'tag' | 'mark-coverage';
  setTaggingSubMode: (subMode: 'tag' | 'mark-coverage') => void;
}

export const EditorModeContext = createContext<EditorModeContextValue | undefined>(undefined);

export const EditorModeContextProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useEditorNavigate();
  const search = useEditorSearch();
  const isTranslation = !!search.translation;
  const startInTagging = !!search.taggingMode && !isTranslation;
  const [mode, setMode] = useState<'tagging' | 'editing'>(startInTagging ? 'tagging' : 'editing');
  const [taggingSubMode, setTaggingSubMode] = useState(startInTagging ? search.taggingSubMode ?? 'tag' : 'tag');

  const setTaggingMode = useCallback(() => {
    setMode('tagging');
  }, []);

  const setEditingMode = useCallback(() => {
    setMode('editing');
  }, []);

  const isTaggingMode = mode === 'tagging';
  const isEditingMode = mode === 'editing';

  useEffect(() => {
    if (isTranslation) setMode('editing');
  }, [isTranslation]);

  useEffect(() => {
    if (mode !== 'tagging') setTaggingSubMode('tag');
  }, [mode]);

  useEffect(() => {
    const isTagging = mode === 'tagging';
    const nextTaggingMode = isTagging ? true : undefined;
    const nextSubMode = isTagging && taggingSubMode === 'mark-coverage' ? ('mark-coverage' as const) : undefined;
    if ((search.taggingMode ?? false) === (nextTaggingMode ?? false) && search.taggingSubMode === nextSubMode) return;
    navigate({ replace: true, hash: true, search: (prev) => ({ ...prev, taggingMode: nextTaggingMode, taggingSubMode: nextSubMode }) });
  }, [mode, taggingSubMode, navigate, search.taggingMode, search.taggingSubMode]);

  return (
    <EditorModeContext.Provider
      value={{
        mode,
        setMode,
        setTaggingMode,
        setEditingMode,
        isTaggingMode,
        isEditingMode,
        taggingSubMode,
        setTaggingSubMode,
      }}
    >
      {children}
    </EditorModeContext.Provider>
  );
};

export const useEditorMode = (): EditorModeContextValue => {
  const context = useContext(EditorModeContext);
  if (!context) {
    return {
      mode: 'editing',
      setMode: () => {},
      setTaggingMode: () => {},
      setEditingMode: () => {},
      isTaggingMode: false,
      isEditingMode: true,
      taggingSubMode: 'tag',
      setTaggingSubMode: () => {},
    };
  }
  return context;
};
